import * as zod from 'zod'
import moment from 'moment-timezone'

export const BREAKDOWN_LEVEL_OPTIONS = [
  { label: 'Department', value: 'department' as const },
  { label: 'Location', value: 'location' as const },
  { label: 'Location + Name', value: 'location_plus_name' as const },
  { label: 'Position', value: 'position' as const },
  { label: 'City', value: 'city' as const },
  { label: 'State', value: 'state' as const },
  { label: 'Date', value: 'date' as const },
  { label: 'Shift', value: 'shift' as const },
]

export const WEEK_START_DAY_OPTIONS = [
  { label: 'Monday', value: 'monday' as const },
  { label: 'Tuesday', value: 'tuesday' as const },
  { label: 'Wednesday', value: 'wednesday' as const },
  { label: 'Thursday', value: 'thursday' as const },
  { label: 'Friday', value: 'friday' as const },
  { label: 'Saturday', value: 'saturday' as const },
  { label: 'Sunday', value: 'sunday' as const },
]

export const TIMEZONE_OPTIONS = moment.tz.zonesForCountry('US').map((name) => ({
  label: name,
  value: name,
}))

const BREAKDOWN_LEVELS = BREAKDOWN_LEVEL_OPTIONS.map((option) => option.value)
const WEEK_START_DAYS = WEEK_START_DAY_OPTIONS.map((option) => option.value)

const invoiceConfigValuesShape = {
  breakdownLevels: zod.array(
    zod.enum(BREAKDOWN_LEVELS as [string, ...string[]])
  ),
  useCustomWeek: zod.boolean(),
  weekStartDay: zod.enum(WEEK_START_DAYS as [string, ...string[]]).optional(),
  timezone: zod.string().optional(),
}

const refineCustomWeekFields = (
  data: {
    useCustomWeek: boolean
    weekStartDay?: string
    timezone?: string
  },
  ctx: zod.RefinementCtx
) => {
  if (!data.useCustomWeek) {
    return
  }
  if (!data.weekStartDay) {
    ctx.addIssue({
      code: zod.ZodIssueCode.custom,
      message: 'Week start day is required',
      path: ['weekStartDay'],
    })
  }
  if (!data.timezone) {
    ctx.addIssue({
      code: zod.ZodIssueCode.custom,
      message: 'Timezone is required',
      path: ['timezone'],
    })
  }
}

const refineBreakdownOrCustomWeek = (
  data: {
    breakdownLevels: string[]
    useCustomWeek: boolean
  },
  ctx: zod.RefinementCtx
) => {
  const hasBreakdownLevels = data.breakdownLevels.length > 0
  if (!hasBreakdownLevels && !data.useCustomWeek) {
    ctx.addIssue({
      code: zod.ZodIssueCode.custom,
      message: 'At least one breakdown level or a custom week must be set',
      path: ['breakdownLevels'],
    })
  }
}

const refineOwnerFields = (
  data: {
    ownerType: 'company' | 'companyGroup'
    companyId?: number
    companyGroupId?: number
  },
  ctx: zod.RefinementCtx
) => {
  if (data.ownerType === 'company') {
    if (data.companyId == null) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: 'Company is required',
        path: ['companyId'],
      })
    }
    if (data.companyGroupId != null) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: 'Company group must not be set when owner is a company',
        path: ['companyGroupId'],
      })
    }
  }
  if (data.ownerType === 'companyGroup') {
    if (data.companyGroupId == null) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: 'Company group is required',
        path: ['companyGroupId'],
      })
    }
    if (data.companyId != null) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: 'Company must not be set when owner is a company group',
        path: ['companyId'],
      })
    }
  }
}

export const invoiceConfigValuesSchema = zod
  .object(invoiceConfigValuesShape)
  .superRefine(refineCustomWeekFields)

export type InvoiceConfigValuesSchema = zod.infer<
  typeof invoiceConfigValuesSchema
>

export const createInvoiceConfigFlowSchema = zod
  .object({
    ownerType: zod.enum(['company', 'companyGroup']),
    companyId: zod.number().optional(),
    companyGroupId: zod.number().optional(),
    ...invoiceConfigValuesShape,
  })
  .superRefine(refineCustomWeekFields)
  .superRefine(refineBreakdownOrCustomWeek)
  .superRefine(refineOwnerFields)

export type CreateInvoiceConfigFlowSchema = zod.infer<
  typeof createInvoiceConfigFlowSchema
>

export const toInvoiceConfigCustomWeek = (data: InvoiceConfigValuesSchema) =>
  data.useCustomWeek && data.weekStartDay && data.timezone
    ? { weekStartDay: data.weekStartDay, timezone: data.timezone }
    : null

export const toInvoiceConfigOwnerIds = (
  data: Pick<
    CreateInvoiceConfigFlowSchema,
    'ownerType' | 'companyId' | 'companyGroupId'
  >
) => ({
  companyId: data.ownerType === 'company' ? data.companyId ?? null : null,
  companyGroupId:
    data.ownerType === 'companyGroup' ? data.companyGroupId ?? null : null,
})
